import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Package, 
  Eye, MousePointer, Calendar, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple bar chart component
const BarChart = ({ data, label, color = 'blue' }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className="text-xs text-slate-400 w-16 shrink-0">{item.label}</span>
          <div className="flex-1 h-6 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
              style={{ width: `${(item.value / max) * 100}%` }}
            />
          </div>
          <span className="text-sm text-white w-16 text-right">{item.value}</span>
        </div>
      ))}
    </div>
  );
};

// Stat card with trend
const StatCard = ({ title, value, prefix = '', suffix = '', trend, icon: Icon, color = 'blue' }) => {
  const trendPositive = trend > 0;
  
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">
            {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
          </p>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trendPositive ? 'text-green-400' : 'text-red-400'}`}>
              {trendPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              <span>{Math.abs(trend)}% vs last period</span>
            </div>
          )}
        </div>
        <div className={`p-3 bg-${color}-500/20 rounded-lg`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
      </div>
    </div>
  );
};

export default function MerchantAnalytics() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [analytics, setAnalytics] = useState({
    overview: {
      total_revenue: 0,
      total_sales: 0,
      total_views: 0,
      total_clicks: 0,
      conversion_rate: 0,
      avg_order_value: 0
    },
    sales_by_day: [],
    top_products: [],
    revenue_by_category: []
  });

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    const token = localStorage.getItem('merchant_token');
    
    try {
      // In a real app, this would be a dedicated analytics endpoint
      // For now, we'll construct data from the dashboard endpoint
      const dashRes = await fetch(`${API_URL}/api/merchant/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dashData = await dashRes.json();

      if (dashData.success) {
        const stats = dashData.dashboard.stats;
        const orders = dashData.dashboard.recent_orders || [];
        
        // Generate mock analytics data based on real stats
        const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const salesByDay = daysOfWeek.map(day => ({
          label: day,
          value: Math.floor(Math.random() * (stats.total_sales + 5))
        }));

        const categories = ['Electronics', 'Fashion', 'Home', 'Sports', 'Beauty'];
        const revenueByCategory = categories.map(cat => ({
          label: cat,
          value: Math.floor(Math.random() * 1000)
        })).sort((a, b) => b.value - a.value);

        setAnalytics({
          overview: {
            total_revenue: stats.total_revenue || 0,
            total_sales: stats.total_sales || 0,
            total_views: Math.floor(Math.random() * 5000) + 1000,
            total_clicks: Math.floor(Math.random() * 500) + 100,
            conversion_rate: stats.total_sales > 0 ? ((stats.total_sales / (Math.random() * 500 + 100)) * 100).toFixed(1) : 0,
            avg_order_value: stats.total_sales > 0 ? (stats.total_revenue / stats.total_sales).toFixed(2) : 0
          },
          sales_by_day: salesByDay,
          top_products: [],
          revenue_by_category: revenueByCategory
        });
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="merchant-analytics">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Analytics Overview</h2>
        <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
          {['week', 'month', 'year'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                period === p 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard 
          title="Total Revenue"
          value={analytics.overview.total_revenue}
          prefix="$"
          trend={12.5}
          icon={DollarSign}
          color="green"
        />
        <StatCard 
          title="Total Sales"
          value={analytics.overview.total_sales}
          trend={8.2}
          icon={ShoppingBag}
          color="blue"
        />
        <StatCard 
          title="Product Views"
          value={analytics.overview.total_views}
          trend={-3.1}
          icon={Eye}
          color="purple"
        />
        <StatCard 
          title="Link Clicks"
          value={analytics.overview.total_clicks}
          trend={15.7}
          icon={MousePointer}
          color="orange"
        />
        <StatCard 
          title="Conversion Rate"
          value={analytics.overview.conversion_rate}
          suffix="%"
          trend={2.3}
          icon={TrendingUp}
          color="cyan"
        />
        <StatCard 
          title="Avg Order Value"
          value={analytics.overview.avg_order_value}
          prefix="$"
          trend={5.8}
          icon={Package}
          color="pink"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales by Day */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="font-semibold text-white mb-4">Sales by Day</h3>
          <div className="h-64 flex items-end justify-between gap-2">
            {analytics.sales_by_day.map((day, index) => {
              const max = Math.max(...analytics.sales_by_day.map(d => d.value), 1);
              const height = (day.value / max) * 100;
              return (
                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full bg-slate-700 rounded-t-lg relative" style={{ height: '200px' }}>
                    <div 
                      className="absolute bottom-0 w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all duration-500"
                      style={{ height: `${height}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-400">{day.label}</span>
                  <span className="text-xs text-white font-medium">{day.value}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Revenue by Category */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="font-semibold text-white mb-4">Revenue by Category</h3>
          <div className="space-y-4">
            {analytics.revenue_by_category.map((cat, index) => {
              const max = analytics.revenue_by_category[0]?.value || 1;
              const percentage = (cat.value / max) * 100;
              const colors = ['blue', 'purple', 'green', 'orange', 'pink'];
              return (
                <div key={index}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">{cat.label}</span>
                    <span className="text-white font-medium">${cat.value}</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-${colors[index % colors.length]}-500 rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Commission Tracking */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="font-semibold text-white mb-4">Commission Earnings</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-slate-900/50 rounded-lg">
            <p className="text-slate-400 text-sm">This {period}</p>
            <p className="text-xl font-bold text-green-400 mt-1">
              ${(analytics.overview.total_revenue * 0.1).toFixed(2)}
            </p>
          </div>
          <div className="p-4 bg-slate-900/50 rounded-lg">
            <p className="text-slate-400 text-sm">Pending Payout</p>
            <p className="text-xl font-bold text-yellow-400 mt-1">
              ${(analytics.overview.total_revenue * 0.08).toFixed(2)}
            </p>
          </div>
          <div className="p-4 bg-slate-900/50 rounded-lg">
            <p className="text-slate-400 text-sm">Total Paid Out</p>
            <p className="text-xl font-bold text-white mt-1">
              ${(analytics.overview.total_revenue * 0.02).toFixed(2)}
            </p>
          </div>
          <div className="p-4 bg-slate-900/50 rounded-lg">
            <p className="text-slate-400 text-sm">Commission Rate</p>
            <p className="text-xl font-bold text-blue-400 mt-1">10%</p>
          </div>
        </div>
      </div>
    </div>
  );
}
